import { useT } from '@/shared/lib/i18n';
import { PhaseHeader, Postit } from '@/shared/ui';

export type DistributionHeaderProps = {
  /** 1-based step in the round. */
  step: number;
  total: number;
};

/**
 * Distribution-phase header shared by `PassDeviceCard` and
 * `DossierReveal` so the round/turn context (Phase 01 · Distribution
 * + TOUR n/N post-it + step dots) stays pinned to the top across both
 * stages of the word-reveal flow.
 */
export const DistributionHeader = ({ step, total }: DistributionHeaderProps) => {
  const t = useT();
  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <PhaseHeader
          eyebrow={t('round.distribution.phase_eyebrow')}
          title={t('round.distribution.title')}
          descriptor={t('round.distribution.descriptor', {
            step: String(step).padStart(2, '0'),
            total: String(total).padStart(2, '0'),
          })}
        />
        <div className="-translate-y-1/2 absolute top-1/2 right-3 z-20">
          <Postit tone="yellow" rot={6} w={68} withPin pinColor="var(--color-stamp-red)">
            <div className="text-center font-display text-[10px] text-ink leading-none tracking-[0.18em]">
              {t('round.distribution.tour')}
            </div>
            <div className="mt-0.5 text-center font-display font-extrabold text-[18px] text-ink leading-none">
              {step}/{total}
            </div>
          </Postit>
        </div>
      </div>
      <div className="px-4">
        <ProgressDots step={step} total={total} />
      </div>
    </div>
  );
};

const ProgressDots = ({ step, total }: { step: number; total: number }) => {
  const dots = Array.from({ length: total }, (_, i) => i);
  return (
    <div className="paper-dossier mx-auto flex w-full max-w-[380px] items-center justify-center gap-2.5 rounded-full px-4 py-2">
      {dots.map((i) => {
        const active = i === step - 1;
        const done = i < step - 1;
        return (
          <span
            key={i}
            aria-hidden
            className="inline-block h-2.5 w-2.5 rounded-full border border-ink/40"
            style={{
              background: active
                ? 'var(--color-orange-mark)'
                : done
                  ? 'var(--color-ink)'
                  : 'transparent',
            }}
          />
        );
      })}
    </div>
  );
};
